import { useBrainStore } from '@/state/brainStore';
import type { SalePayment, SettlementLump, StudioId } from '@/domain/types';

/**
 * Settlement matching (Prompt E4). The money actually landing: a bank lump
 * arrives a day or two after the payments and the system splits it by the sale
 * reference stamped on each transaction, ticking the lines off automatically.
 * Accounts only ever look at the two or three that did not match.
 *
 * PROVIDER-AGNOSTIC by contract — nothing here (or in the types) names a card
 * provider. The provider differences (landing delay, weekend gap) are seeded
 * DATA characteristics, not code paths.
 */

/** Hours a card payment can sit unsettled before it is flagged overdue. */
const OVERDUE_HOURS = 72;

/**
 * The CARD portion of a sale — what the terminal charged, so what its docket and
 * its settlement line should show (W7 n1: the one shared rule for the terminal
 * echo, the docket match and the bank split). The deposit was paid earlier and
 * is excluded. A card sale charges the whole collect-now total (balance + merch
 * + any signed adjustment); a mixed sale only what was left after the cash
 * (cash stays in the till); cash / rolled-deposit charge nothing to a card.
 */
export function cardPortionAud(sale: SalePayment): number {
  const collectNow = sale.balanceAud + sale.merchAud + (sale.adjustmentAud ?? 0);
  if (sale.method === 'card') return collectNow;
  if (sale.method === 'mixed') return Math.max(0, collectNow - sale.cashInAud);
  return 0;
}

/** A settlement line after matching: either tied to a system payment, or an orphan. */
export interface SplitLine {
  reference: string;
  amountAud: number;
  terminal: string;
  paidAt: string;
  /** True when a system SalePayment with this reference + amount exists. */
  matched: boolean;
  /** The matched system record, when there is one. */
  sale?: SalePayment;
}

export interface LumpSplit {
  lump: SettlementLump;
  lines: SplitLine[];
  matched: SplitLine[];
  /** Lines the platform echoed with no matching system record. */
  orphans: SplitLine[];
  matchedCount: number;
  totalLines: number;
}

/**
 * Pure: split a lump against a set of candidate payments by reference + amount.
 * A line matches when a payment carries the same reference AND the same charge;
 * a line with no matching payment is an orphan. Kept pure so the split is
 * trivially testable — feed it lines and payments, read back the classification.
 */
export function splitLump(lump: SettlementLump, payments: readonly SalePayment[]): LumpSplit {
  const byRef = new Map(payments.map((p) => [p.saleReference, p]));
  const lines: SplitLine[] = lump.lines.map((l) => {
    const sale = byRef.get(l.reference);
    const matched = sale !== undefined && cardPortionAud(sale) === l.amountAud;
    return {
      reference: l.reference,
      amountAud: l.amountAud,
      terminal: l.terminal,
      paidAt: l.paidAt,
      matched,
      sale: matched ? sale : undefined,
    };
  });
  const matched = lines.filter((l) => l.matched);
  const orphans = lines.filter((l) => !l.matched);
  return { lump, lines, matched, orphans, matchedCount: matched.length, totalLines: lines.length };
}

/** Every lump for a studio, split against its make-sales, newest landing first. */
export function studioSettlement(studioId: StudioId): LumpSplit[] {
  const brain = useBrainStore.getState();
  const payments = Object.values(brain.salePayments).filter((p) => p.studioId === studioId);
  return Object.values(brain.settlementLumps)
    .filter((l) => l.studioId === studioId)
    .sort((a, b) => b.landedOn.localeCompare(a.landedOn))
    .map((l) => splitLump(l, payments));
}

/** A card payment the system has recorded but no lump has echoed yet. */
export interface UnsettledPayment {
  sale: SalePayment;
  ageHours: number;
  /** True once the payment is older than the landing window should allow. */
  overdue: boolean;
}

/**
 * Card/mixed payments with no settlement line anywhere yet. Recent ones are
 * normal (the lump has not landed — settlement lags 24-36h and skips weekends);
 * only the ones past the window are flagged overdue for the office to chase.
 * After-cutoff sales are excluded — they belong to tomorrow's lump by design.
 */
export function unsettledPayments(studioId: StudioId, nowIso: string): UnsettledPayment[] {
  const brain = useBrainStore.getState();
  const settledRefs = new Set<string>();
  for (const lump of Object.values(brain.settlementLumps)) {
    if (lump.studioId !== studioId) continue;
    for (const line of lump.lines) settledRefs.add(line.reference);
  }
  const now = new Date(nowIso).getTime();
  return Object.values(brain.salePayments)
    .filter(
      (p) =>
        p.studioId === studioId &&
        (p.method === 'card' || p.method === 'mixed') &&
        !p.afterCutoff &&
        !settledRefs.has(p.saleReference),
    )
    .map((p) => {
      const ageHours = (now - new Date(p.takenAt).getTime()) / 3_600_000;
      return { sale: p, ageHours, overdue: ageHours > OVERDUE_HOURS };
    })
    .sort((a, b) => b.ageHours - a.ageHours);
}
